/* eslint-disable no-dupe-keys */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useMemo, useEffect } from "react";
import {
  View,
  Text,
  FlatList,
  ScrollView,
  StyleSheet,
  StyleProp,
  ViewStyle,
  TouchableOpacity,
  TouchableWithoutFeedback,
  useWindowDimensions,
  Platform,
} from "react-native";
import LHOverlay from "./LHOverlay";
import LhButton from "./LhButton";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import FontFamily from "../constants/fonts";
import useSyncedHorizontalScrollbar from "../hooks/useSyncedHorizontalScrollbar";
import { useTheme } from "../theme";

// Types
type ColumnType = "toggle" | "checkbox";

type Column = {
  columnKey: string;
  header: string;
  type?: ColumnType;
  renderCell?: (item: any) => React.ReactNode;
  formatter?: (value: any) => string;
  flexGrow?: number; // Optional prop to define how much space a column should take relative to others
  width?: number; // Optional fixed override width (respected as minWidth in calculations)
  getSortValue?: (item: any) => any; // Custom sort value extractor for columns whose key differs from data field
  sortable?: boolean; // Set to false to disable sorting for this column
};

type CheckboxField = "yesChecked" | "noChecked" | "naChecked";

type Props = {
  data: any[];
  columns: Column[];
  tableHeader: string;
  tableDescription: string;
  // Optional locale labels for pagination and related UI
  locale?: {
    rowsPerPageLabel?: string;
    pageLabel?: string;
    pageOfLabel?: string;
    firstPageLabel?: string;
    prevPageLabel?: string;
    nextPageLabel?: string;
    lastPageLabel?: string;
  };
  maxRecordsToShow?: number;
  onRowClick?: (rowData: any) => void;
  columnFlexValues?: { [columnKey: string]: number }; // Optional flex values for columns
  showHeaderTopBorder?: boolean;
  expansionRenderer?: (item: any) => React.ReactNode; // NEW optional prop for inline row expansion
  disableRowHighlight?: boolean;
  highContrastText?: boolean;
};

const LHDataTable = ({
  data,
  columns,
  maxRecordsToShow = 10,
  onRowClick,
  disableRowHighlight = false,
  columnFlexValues,
  showHeaderTopBorder = true,
  expansionRenderer,
  locale,
  highContrastText = false,
}: Props) => {
  const { resolvedTheme, colors } = useTheme();
  const useHighContrastText = highContrastText && resolvedTheme === "dark";
  const isDark = resolvedTheme === 'dark';
  const headerTextColor = isDark ? '#FFFFFF' : '#616161';
  const brandPrimary = colors.brand.primary;

  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null);
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "asc" | "desc";
  } | null>(null);
  const [overlayVisible, setOverlayVisible] = useState(false);
  const [overlayContent, setOverlayContent] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(maxRecordsToShow);
  useEffect(() => {
    setRowsPerPage(maxRecordsToShow);
  }, [maxRecordsToShow]);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [columnWidths, setColumnWidths] = useState<{
    [key: string]: { minWidth: number; flex: number };
  }>({});
  const [layoutCalculated, setLayoutCalculated] = useState(false);
  const { tableScrollRef, topScrollBarRef, contentWidth, handleContentWidthChange } =
    useSyncedHorizontalScrollbar();

  const rowsPerPageLabel = locale?.rowsPerPageLabel ?? 'Rows per page';
  const pageLabel = locale?.pageLabel ?? 'Page';
  const pageOfLabel = locale?.pageOfLabel ?? 'of';
  const firstPageLabel = locale?.firstPageLabel ?? 'First page';
  const prevPageLabel = locale?.prevPageLabel ?? 'Previous page';
  const nextPageLabel = locale?.nextPageLabel ?? 'Next page';
  const lastPageLabel = locale?.lastPageLabel ?? 'Last page';

  const { width: screenWidth } = useWindowDimensions();

  // Helper to detect iOS mobile (phone) vs iPad/web
  const isIOSMobile = Platform.OS === "ios" && screenWidth < 768;
  // iPad/tablet native dark mode only — web uses the global CSS sweep,
  // iPhone is excluded (short side < 768). Applies to iPad and large Android tablets.
  const isTabletDark = isDark && Platform.OS !== 'web' && screenWidth >= 768;

  // Enhanced estimateTextWidth function
  const estimateTextWidth = (text: string, isBold: boolean = false) => {
    const baseWidth = Platform.OS === "web" ? 8 : 6; // Average character width in pixels
    const boldMultiplier = isBold ? 1.2 : 1;

    // Count characters considering some might be wider
    let charCount = 0;
    const str = String(text);

    for (let i = 0; i < str.length; i++) {
      // Wider characters (like W, M)
      if (/[WM]/.test(str[i])) {
        charCount += 1.3;
      }
      // Narrow characters (like i, l, 1)
      else if (/[ilt1|]/.test(str[i])) {
        charCount += 0.6;
      }
      // Average characters
      else {
        charCount += 1;
      }
    }

    return charCount * baseWidth * boldMultiplier;
  };
  // Enhanced calculateColumnWidths
  const calculateColumnWidths = useMemo(() => {
    const widths: { [key: string]: { minWidth: number; flex: number } } = {};
    const padding = 24; // Increased horizontal padding to ensure text has enough room
    const minColumnWidth = 120; // Increased minimum width
    const maxColumnWidth = 300;

    // Ensure data is an array to prevent forEach errors
    const safeData = Array.isArray(data) ? data : [];

    columns.forEach((col) => {
      // Calculate header width
      let maxWidth = estimateTextWidth(col.header, true) + padding;

      // Calculate max content width
      safeData.forEach((item) => {
        let cellValue = item[col.columnKey];
        if (col.formatter && cellValue !== undefined && cellValue !== null) {
          cellValue = col.formatter(cellValue);
        }
        if (cellValue !== undefined && cellValue !== null) {
          const cellWidth = estimateTextWidth(String(cellValue)) + padding;
          maxWidth = Math.max(maxWidth, cellWidth);
        }
      });

      // If column has an explicit width override, use it directly
      if (col.width !== undefined) {
        maxWidth = col.width;
      } else {
        // Apply constraints from text estimation
        maxWidth = Math.max(minColumnWidth, Math.min(maxWidth, maxColumnWidth));
      }

      // Determine flex value
      // When an explicit col.width is set, use flex:0 so the column stays at
      // exactly that width and never absorbs extra space (prevents gap on web).
      let flex = 1;
      if (col.width !== undefined) {
        flex = 0;
      } else if (columnFlexValues?.[col.columnKey]) {
        flex = columnFlexValues[col.columnKey];
      } else if (col.flexGrow !== undefined) {
        flex = col.flexGrow;
      } else if (maxWidth > 200) {
        flex = 2;
      } else if (maxWidth > 150) {
        flex = 1.5;
      }

      widths[col.columnKey] = {
        minWidth: maxWidth,
        flex,
      };
    });

    return widths;
  }, [data, columns, screenWidth, columnFlexValues]);

  // Update column widths when dependencies change
  useEffect(() => {
    setColumnWidths(calculateColumnWidths);
  }, [calculateColumnWidths]);

  const monthNameToIndex = (month: string): number => {
    const normalized = month.trim().toLowerCase();
    const months: Record<string, number> = {
      jan: 0,
      january: 0,
      feb: 1,
      february: 1,
      mar: 2,
      march: 2,
      apr: 3,
      april: 3,
      may: 4,
      jun: 5,
      june: 5,
      jul: 6,
      july: 6,
      aug: 7,
      august: 7,
      sep: 8,
      sept: 8,
      september: 8,
      oct: 9,
      october: 9,
      nov: 10,
      november: 10,
      dec: 11,
      december: 11,
    };

    return normalized in months ? months[normalized] : -1;
  };

  const parseUSDateToTimestamp = (value: unknown): number | null => {
    if (value instanceof Date) {
      const ts = value.getTime();
      return Number.isNaN(ts) ? null : ts;
    }

    if (typeof value !== "string") {
      return null;
    }

    const raw = value.trim();
    if (!raw) {
      return null;
    }

    // Handles values like "Jun 04, 2026, 05:47 PM"
    const monthNameMatch = raw.match(
      /^([A-Za-z]{3,9})\s+(\d{1,2}),\s*(\d{4})(?:,\s*(\d{1,2}):(\d{2})\s*(AM|PM))?$/i
    );

    if (monthNameMatch) {
      const [, monthName, dayStr, yearStr, hourStr, minuteStr, amPmStr] = monthNameMatch;
      const monthIndex = monthNameToIndex(monthName);
      if (monthIndex >= 0) {
        const day = Number(dayStr);
        const year = Number(yearStr);
        let hour = hourStr ? Number(hourStr) : 0;
        const minute = minuteStr ? Number(minuteStr) : 0;
        const amPm = amPmStr?.toUpperCase();

        if (amPm === "PM" && hour < 12) hour += 12;
        if (amPm === "AM" && hour === 12) hour = 0;

        const date = new Date(year, monthIndex, day, hour, minute, 0, 0);
        if (
          date.getFullYear() === year &&
          date.getMonth() === monthIndex &&
          date.getDate() === day
        ) {
          return date.getTime();
        }
      }
    }

    // Handles US numeric dates: MM/DD/YYYY and optional time.
    const usNumericMatch = raw.match(
      /^(\d{1,2})[\/-](\d{1,2})[\/-](\d{2}|\d{4})(?:[\s,]+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?)?$/i
    );

    if (usNumericMatch) {
      const [, monthStr, dayStr, yearStr, hourStr, minuteStr, secondStr, amPmStr] = usNumericMatch;

      const month = Number(monthStr);
      const day = Number(dayStr);
      const year = yearStr.length === 2 ? Number(`20${yearStr}`) : Number(yearStr);
      let hour = hourStr ? Number(hourStr) : 0;
      const minute = minuteStr ? Number(minuteStr) : 0;
      const second = secondStr ? Number(secondStr) : 0;
      const amPm = amPmStr?.toUpperCase();

      if (amPm === "PM" && hour < 12) hour += 12;
      if (amPm === "AM" && hour === 12) hour = 0;

      const monthIndex = month - 1;
      const date = new Date(year, monthIndex, day, hour, minute, second, 0);
      if (
        month >= 1 &&
        month <= 12 &&
        day >= 1 &&
        day <= 31 &&
        date.getFullYear() === year &&
        date.getMonth() === monthIndex &&
        date.getDate() === day
      ) {
        return date.getTime();
      }
    }

    const fallback = Date.parse(raw);
    return Number.isNaN(fallback) ? null : fallback;
  };

  const sortedData = useMemo(() => {
    if (!sortConfig) {
      return data;
    }
    return [...data].sort((a, b) => {
      // Find the column object with the matching columnKey
      const column = columns.find((col) => col.columnKey === sortConfig.key);
      if (!column) {
        return 0;
      }

      const aValue = column.getSortValue ? column.getSortValue(a) : a[column.columnKey];
      const bValue = column.getSortValue ? column.getSortValue(b) : b[column.columnKey];

      // Handle null/undefined — push to bottom regardless of direction
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return sortConfig.direction === "asc" ? 1 : -1;
      if (bValue == null) return sortConfig.direction === "asc" ? -1 : 1;

      // Numeric comparison
      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortConfig.direction === "asc" ? aValue - bValue : bValue - aValue;
      }

      // Date instance comparison
      if (aValue instanceof Date && bValue instanceof Date) {
        return sortConfig.direction === "asc"
          ? aValue.getTime() - bValue.getTime()
          : bValue.getTime() - aValue.getTime();
      }

      const aDateTs = parseUSDateToTimestamp(aValue);
      const bDateTs = parseUSDateToTimestamp(bValue);
      if (aDateTs !== null && bDateTs !== null) {
        return sortConfig.direction === "asc"
          ? aDateTs - bDateTs
          : bDateTs - aDateTs;
      }

      // String comparison (case-insensitive)
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();
      if (aStr < bStr) return sortConfig.direction === "asc" ? -1 : 1;
      if (aStr > bStr) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [data, sortConfig, columns]);

  const totalPages = Math.ceil(sortedData.length / rowsPerPage);
  const paginatedData = sortedData.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );
  const handleSort = (columnKey: string) => {
    if (sortConfig?.key === columnKey) {
      setSortConfig({
        key: columnKey,
        direction: sortConfig.direction === "asc" ? "desc" : "asc",
      });
    } else {
      setSortConfig({ key: columnKey, direction: "asc" });
    }
  };

  const showOverlayWithCustomContent = (content: any) => {
    setOverlayContent(content);
    setOverlayVisible(true);
  };

  const closeOverlay = () => {
    setOverlayVisible(false);
    setOverlayContent(null);
  };

  // Function to get status style based on status value
  const getStatusStyle = (status: string) => {
    // Normalize status: trim, lowercase, and replace underscores/hyphens with spaces
    const normalizedStatus = status
      ?.toString()
      .trim()
      .toLowerCase()
      .replace(/[_-]+/g, " ");

    switch (normalizedStatus) {
      case "in progress":
        return {
          container: {
            backgroundColor: "#E5A408",
            height: 24,
            paddingHorizontal: 8,
            paddingVertical: 2,
            borderRadius: 12,
            alignItems: "center" as const,
            justifyContent: "center" as const,
            overflow: "hidden" as const,
            flexDirection: "row" as const,
            alignSelf: "flex-start" as const,
            minWidth: 80,
            maxWidth: 200,
          },
          text: {
            color: "#000000",
            fontWeight: "600" as const,
            fontSize: 12,
            textAlign: "center" as const,
            lineHeight: 16,
          },
        };
      case "completed":
      case "complete":
        return {
          container: {
            backgroundColor: "#027C06",
            height: 24,
            paddingHorizontal: 8,
            paddingVertical: 2,
            borderRadius: 12,
            alignItems: "center" as const,
            justifyContent: "center" as const,
            overflow: "hidden" as const,
            flexDirection: "row" as const,
            alignSelf: "flex-start" as const,
            minWidth: 80,
            maxWidth: 200,
          },
          text: {
            color: "#000000",
            fontWeight: "600" as const,
            fontSize: 12,
            textAlign: "center" as const,
            lineHeight: 16,
          },
        };
      case "active":
        return {
          container: {
            backgroundColor: brandPrimary,
            height: 24,
            paddingHorizontal: 8,
            paddingVertical: 2,
            borderRadius: 12,
            alignItems: "center" as const,
            justifyContent: "center" as const,
            overflow: "hidden" as const,
            flexDirection: "row" as const,
            alignSelf: "flex-start" as const,
            minWidth: 80,
            maxWidth: 200,
          },
          text: {
            color: "#FFFFFF",
            fontWeight: "600" as const,
            fontSize: 12,
            textAlign: "center" as const,
            lineHeight: 16,
          },
        };
      default:
        return {
          container: {
            backgroundColor: "#E5A408",
            height: 24,
            paddingHorizontal: 8,
            paddingVertical: 2,
            borderRadius: 12,
            alignItems: "center" as const,
            justifyContent: "center" as const,
            overflow: "hidden" as const,
            flexDirection: "row" as const,
            alignSelf: "flex-start" as const,
            minWidth: 80,
            maxWidth: 200,
          },
          text: {
            color: "#000000",
            fontWeight: "600" as const,
            fontSize: 12,
            textAlign: "center" as const,
            lineHeight: 16,
          },
        };
    }
  };

  const renderRow = ({ item, index }: { item: any; index: number }) => {
    const isSelected = selectedRowIndex === index;

    // Debug logging to see what we're working with
    if (index === 0) {
      console.log("First row item:", item);
      console.log(
        "Columns:",
        columns.map((col) => ({ key: col.columnKey, header: col.header }))
      );
    }

    const handleCellClick = (col: Column, cellValue: any) => {
      // Handle "type" column separately if needed
      if (col.columnKey === "type") {
        // Pass the raw item without any special flags
        if (onRowClick) {
          onRowClick({ ...item, isPulseCheckNavigation: true });
        }
        return;
      }

      // If the column key is "Title" or "Shift", handle differently - navigate to ShiftHandover
      if (col.columnKey === "Title" || col.columnKey === "Shift") {
        if (onRowClick) {
          onRowClick({
            ...item,
            isShiftHandOverNavigation: true,
            columnKey: col.columnKey,
          });
        }
        return;
      }

      // For all other columns and values, use the default behavior
      setSelectedRowIndex(index);

      // If there's a click handler, pass the item with the clicked column info
      if (onRowClick) {
        onRowClick({ ...item, columnKey: col.columnKey });
      } else {
        showOverlayWithCustomContent(item);
      }
    };

    const rowView = (
      <View
        style={[
          styles.row,
          isSelected && !disableRowHighlight && styles.selectedRow,
          isSelected && !disableRowHighlight && isTabletDark && { backgroundColor: 'rgba(61,139,255,0.15)' },
          isTabletDark && { borderBottomColor: '#363636' },
          isDark && Platform.OS === 'web' && { borderBottomColor: '#363636' },
          isDark && isIOSMobile && { borderBottomColor: '#363636' },
        ]}
        testID="table-row"
        key={`row-${index}`}
      >
        {columns.map((col) => (
          <View
            key={col.columnKey}
            style={[
              styles.cellContainer,
              {
                justifyContent: "flex-start", // Force left alignment for all cell containers
              },
              columnWidths[col.columnKey] && {
                minWidth: columnWidths[col.columnKey].minWidth,
                flex: columnWidths[col.columnKey].flex,
                // On web, omit fixed width so flex fills the container (prevents right-side gap)
                ...(Platform.OS !== "web" && { width: columnWidths[col.columnKey].minWidth }),
                height:
                  Platform.OS === "ios"
                    ? 90
                    : col.header === "Zone"
                    ? "auto"
                    : 48,
              },
            ]}
          >
            {col.renderCell ? (
              <React.Fragment key={`custom-cell-${col.columnKey}-${index}`}>
                {col.renderCell(item)}
              </React.Fragment>
            ) : col.columnKey === "action" &&
              item[col.columnKey] === "Resume" ? (
              <LhButton
                key={`action-button-${index}`}
                size="small"
                text={item[col.columnKey]}
                style={{ width: 100 }}
                onPress={() => console.log(`Action clicked for row ${index}`)}
              />
            ) : (
              <TouchableOpacity
                key={`cell-${col.columnKey}-${index}`}
                onPress={() => handleCellClick(col, item[col.columnKey])}
                style={[
                  styles.cellTouchable,
                  {
                    alignItems: "flex-start", // Force left alignment for all touchable cells
                    width: "100%",
                  },
                ]}
              >
                {(() => {
                  const colKeyLower = String(col.columnKey ?? "").toLowerCase();
                  const headerLower = String(col.header ?? "").toLowerCase();

                  // Treat only explicit Status/Action columns as the status pill.
                  const isStatusColumn =
                    colKeyLower === "status" ||
                    colKeyLower === "action" ||
                    headerLower === "status";

                  const cellValue = item[col.columnKey];

                  if (isStatusColumn) {
                    // Backend currently returns STATUS: null on first create.
                    // Ensure the pill always has visible text and style.
                    const rawText =
                      col.formatter && cellValue !== undefined && cellValue !== null
                        ? col.formatter(cellValue)
                        : cellValue;

                    const displayText =
                      rawText === null ||
                      rawText === undefined ||
                      String(rawText).trim().length === 0
                        ? "In Progress"
                        : String(rawText);

                    const statusStyle = getStatusStyle(displayText);

                    return (
                      <View
                        style={statusStyle.container}
                        onLayout={() =>
                          !layoutCalculated && setLayoutCalculated(true)
                        }
                      >
                        <Text
                          key={`text-${col.columnKey}-${index}`}
                          style={statusStyle.text}
                          numberOfLines={1}
                          adjustsFontSizeToFit={false}
                          ellipsizeMode="tail"
                        >
                          {displayText}
                        </Text>
                      </View>
                    );
                  }

                  return (
                    <Text
                      key={`text-${col.columnKey}-${index}`}
                      style={[
                        styles.cell,
                        {
                          textAlign: "left", // Force left alignment for all data cells
                        },
                        col.columnKey === "type" &&
                        col.columnKey !== columns[0].columnKey
                          ? styles.typeCell
                          : {},
                        col.columnKey === columns[0].columnKey
                          ? styles.firstCellText
                          : styles.otherCellText,
                        // Web dark mode: ensure the first-column (TYPE) link blue is preserved.
                        // globalDarkModeWeb now maps #005CBC text → #3D8BFF via BRAND_LOOKUP, but
                        // this explicit inline override acts as a belt-and-suspenders guarantee
                        // so CSS insertion-order races cannot let white win.
                        isDark && Platform.OS === 'web' && col.columnKey === columns[0].columnKey &&
                          { color: '#3D8BFF' },
                        // Web dark mode: make non-first-column cell text white/light
                        isDark && Platform.OS === 'web' && col.columnKey !== columns[0].columnKey &&
                          { color: '#E0E0E0' },
                        // iPad dark mode: first column keeps brand blue, others go white/light
                        isTabletDark && col.columnKey === columns[0].columnKey &&
                          { color: '#3D8BFF' },
                        isTabletDark && col.columnKey !== columns[0].columnKey && { color: '#E0E0E0' },
                        // Mobile iOS dark mode: first column keeps brand blue, others go white/light
                        isDark && isIOSMobile && col.columnKey === columns[0].columnKey && { color: '#3D8BFF' },
                        isDark && isIOSMobile && col.columnKey !== columns[0].columnKey && { color: '#E0E0E0' },
                        useHighContrastText &&
                          (col.columnKey === columns[0].columnKey
                            ? { color: brandPrimary }
                            : { color: colors.text.primary }),
                      ]}
                      numberOfLines={2}
                      adjustsFontSizeToFit={false}
                      ellipsizeMode="tail"
                    >
                      {col.formatter &&
                      cellValue !== undefined &&
                      cellValue !== null
                        ? col.formatter(cellValue)
                        : cellValue !== undefined && cellValue !== null
                          ? String(cellValue)
                          : "-"}
                    </Text>
                  );
                })()}
              </TouchableOpacity>
            )}
          </View>
        ))}
      </View>
    );

    // Render the optional expansion content immediately after the row so it appears inline
    const expansionNode = expansionRenderer ? expansionRenderer(item) : null;

    return (
      <React.Fragment key={`row-frag-${index}`}>
        {rowView}
        {expansionNode}
      </React.Fragment>
    );
  };

  const rowsPerPageOptions = [10, 25, 50, 100];

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1); // Reset to first page when changing rows per page
  };

  // Mobile pagination component based on Figma design
  const renderMobilePagination = () => (
    <View style={styles.mobilePaginationContainer}>
      <View style={styles.mobilePaginationBox}>
        {/* Left Arrow Cell */}
        <TouchableOpacity
          disabled={currentPage === 1}
          onPress={() => setCurrentPage(currentPage - 1)}
          style={[
            styles.mobilePaginationCell,
            styles.mobilePaginationArrowCell,
            currentPage === 1 && styles.mobilePaginationCellDisabled,
          ]}
          testID="mobile-prev-page-button"
          accessibilityLabel="Previous page"
        >
          <MaterialIcons
            name="chevron-left"
            size={16}
            color={currentPage === 1 ? "#A0A0A0" : brandPrimary}
          />
        </TouchableOpacity>

        {/* Current Page Cell */}
        <View
          style={[
            styles.mobilePaginationCell,
            styles.mobilePaginationCurrentCell,
            { backgroundColor: brandPrimary },
          ]}
        >
          <Text style={styles.mobilePaginationCurrentText}>{currentPage}</Text>
        </View>

        {/* Next Page Cell */}
        <View
          style={[
            styles.mobilePaginationCell,
            styles.mobilePaginationNextCell,
            isDark && { backgroundColor: '#2A2A2A' },
          ]}
        >
          <Text style={[styles.mobilePaginationNextText, { color: headerTextColor }]}>
            {currentPage < totalPages ? currentPage + 1 : currentPage}
          </Text>
        </View>

        {/* Right Arrow Cell */}
        <TouchableOpacity
          disabled={currentPage === totalPages}
          onPress={() => setCurrentPage(currentPage + 1)}
          style={[
            styles.mobilePaginationCell,
            styles.mobilePaginationArrowCell,
            currentPage === totalPages && styles.mobilePaginationCellDisabled,
          ]}
          testID="mobile-next-page-button"
          accessibilityLabel="Next page"
        >
          <MaterialIcons
            name="chevron-right"
            size={16}
            color={currentPage === totalPages ? "#A0A0A0" : brandPrimary}
          />
        </TouchableOpacity>
      </View>
    </View>
  );

  // Desktop/Web pagination component (existing)
  const renderDesktopPagination = () => {
    const paginationNavColor = (isTabletDark || (isDark && Platform.OS === 'web')) ? '#3D8BFF' : '#034895';
    const paginationDisabledColor = (isTabletDark || (isDark && Platform.OS === 'web')) ? '#555555' : '#D0D0D0';
    return (
    <View style={[styles.paginationContainer, (isTabletDark || (isDark && Platform.OS === 'web')) && { borderTopColor: '#363636' }]}>
      {/* Left side: Rows per page selector */}
      <View style={styles.rowsPerPageContainer}>
        <Text
          style={[[
            styles.rowsPerPageLabel, { color: headerTextColor }],
            useHighContrastText && { color: colors.text.primary },
          ]}
        >
          Rows per page
        </Text>
        <View style={{ position: "relative" }}>
          <TouchableOpacity
            style={[
              styles.dropdownContainer,
              (isTabletDark || (isDark && Platform.OS === 'web')) && { backgroundColor: '#2A2A2A', borderColor: '#555555' },
            ]}
            onPress={() => setDropdownVisible(!dropdownVisible)}
            testID="rows-per-page-dropdown"
            accessibilityLabel={`${rowsPerPageLabel} selector`}
          >
            <Text
              style={[[
                styles.dropdownValue, { color: headerTextColor }],
                useHighContrastText && { color: colors.text.primary },
              ]}
            >
              {rowsPerPage}
            </Text>
            <MaterialIcons
              name={
                dropdownVisible ? "keyboard-arrow-up" : "keyboard-arrow-down"
              }
              size={20}
              color={headerTextColor}
            />
          </TouchableOpacity>
          {dropdownVisible && (
            <View style={[
              styles.dropdownOptionsContainer,
              (isTabletDark || (isDark && Platform.OS === 'web')) && { backgroundColor: '#2A2A2A', borderColor: '#555555' },
            ]}>
              {rowsPerPageOptions.map((option) => (
                <TouchableOpacity
                  key={option}
                  style={[
                    styles.dropdownOption,
                    option === rowsPerPage && styles.dropdownOptionSelected,
                    (isTabletDark || (isDark && Platform.OS === 'web')) && { borderBottomColor: '#363636' },
                    option === rowsPerPage && (isTabletDark || (isDark && Platform.OS === 'web')) && { backgroundColor: 'rgba(61,139,255,0.15)' },
                  ]}
                  onPress={() => {
                    handleRowsPerPageChange(option);
                    setDropdownVisible(false);
                  }}
                  testID={`rows-per-page-option-${option}`}
                  accessibilityLabel={`${option} ${rowsPerPageLabel}`}
                >
                  <Text
                    style={[
                      styles.dropdownOptionText,
                      (isTabletDark || (isDark && Platform.OS === 'web')) && { color: '#E0E0E0' },
                      option === rowsPerPage && styles.dropdownOptionTextSelected,
                    ]}
                  >
                    {option}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          )}
        </View>
      </View>

      {/* Right side: Page navigation */}
      <View style={styles.pageNavigationContainer}>
          <TouchableOpacity
          disabled={currentPage === 1}
          onPress={() => setCurrentPage(1)}
          style={styles.navigationButtonNoBorder}
          testID="first-page-button"
          accessibilityLabel={firstPageLabel}
        >
          <MaterialIcons
            name="first-page"
            size={20}
            color={currentPage === 1 ? paginationDisabledColor : paginationNavColor}
          />
        </TouchableOpacity>

          <TouchableOpacity
          disabled={currentPage === 1}
          onPress={() => setCurrentPage(currentPage - 1)}
          style={styles.navigationButtonNoBorder}
          testID="prev-page-button"
          accessibilityLabel={prevPageLabel}
        >
          <MaterialIcons
            name="keyboard-arrow-left"
            size={20}
            color={currentPage === 1 ? paginationDisabledColor : paginationNavColor}
          />
        </TouchableOpacity>

        <View style={styles.pageIndicatorContainer}>
          <Text style={[styles.pageText, { color: headerTextColor }]}>Page</Text>
          <View style={[
            styles.currentPageBox,
            (isTabletDark || (isDark && Platform.OS === 'web')) && { backgroundColor: '#2A2A2A', borderColor: '#3D8BFF' },
          ]}>
            <Text
              style={[
                [styles.currentPageText, { color: headerTextColor }],
                useHighContrastText && { color: colors.text.primary },
              ]}
            >
              {currentPage}
            </Text>
          </View>
          <Text
            style={[
              [styles.pageText, { color: headerTextColor }],
              useHighContrastText && { color: colors.text.primary },
            ]}
          >
            of{" "}
            <Text
              style={[
                [styles.totalPagesText, { color: headerTextColor }],
                useHighContrastText && { color: colors.text.primary },
              ]}
            >
              {totalPages}
            </Text>
          </Text>
        </View>

        <TouchableOpacity
          disabled={currentPage === totalPages}
          onPress={() => setCurrentPage(currentPage + 1)}
          style={styles.navigationButtonNoBorder}
          testID="next-page-button"
          accessibilityLabel={nextPageLabel}
        >
          <MaterialIcons
            name="keyboard-arrow-right"
            size={20}
            color={currentPage === totalPages ? paginationDisabledColor : paginationNavColor}
          />
        </TouchableOpacity>

        <TouchableOpacity
          disabled={currentPage === totalPages}
          onPress={() => setCurrentPage(totalPages)}
          style={styles.navigationButtonNoBorder}
          testID="last-page-button"
          accessibilityLabel={lastPageLabel}
        >
          <MaterialIcons
            name="last-page"
            size={20}
            color={currentPage === totalPages ? paginationDisabledColor : paginationNavColor}
          />
        </TouchableOpacity>
      </View>
    </View>
    );
  };

  // Responsive pagination renderer
  const renderPaginationControls = () => {
    // Use mobile pagination only for iOS mobile (phone), desktop pagination for web, iPad, and other platforms
    const isIOSMobile = Platform.OS === "ios" && screenWidth < 768; // 768px is typical iPad breakpoint

    if (isIOSMobile) {
      return renderMobilePagination();
    }
    return renderDesktopPagination();
  };

  return (
    <TouchableWithoutFeedback onPress={() => setDropdownVisible(false)}>
      <View style={styles.container}>
        {/* <Text style={styles.header}>{tableHeader}</Text>
        <Text style={styles.description}>{tableDescription}</Text> */}
        {Platform.OS === "web" &&
          React.createElement(
            "div",
            {
              ref: topScrollBarRef,
              style: { overflowX: "scroll", overflowY: "hidden", height: 14 },
            },
            React.createElement("div", {
              style: { width: Math.max(contentWidth, 1), height: 1 },
            })
          )}
        <ScrollView
          ref={tableScrollRef}
          horizontal
          showsHorizontalScrollIndicator={true}
          style={{ flexGrow: 1 }}
          contentContainerStyle={{ flexGrow: 1 }}
          scrollEventThrottle={16}
          nestedScrollEnabled={true}
          onContentSizeChange={(width) => handleContentWidthChange(width)}
        >
          <View style={Platform.OS === 'web'
            ? { minWidth: "100%" }
            : { width: "100%", minWidth: "100%" }
          }>
            <View style={[
              styles.headerRow,
              showHeaderTopBorder ? { borderTopWidth: 2, borderTopColor: (isTabletDark || (isDark && Platform.OS === 'web')) ? '#363636' : '#D7E5F8' } : { borderTopWidth: 0, borderTopColor: 'transparent' },
              (isTabletDark || (isDark && Platform.OS === 'web') || (isDark && isIOSMobile)) && { borderBottomColor: '#363636' },
            ]}>
              {columns.map((col) => {
                const isSorted = sortConfig?.key === col.columnKey;
                const isSortable = col.sortable !== false;
                const cellStyle: StyleProp<ViewStyle> = [
                  styles.cellContainer,
                  {
                    justifyContent: isIOSMobile ? "center" : "flex-start",
                  },
                  columnWidths[col.columnKey] && {
                    minWidth: columnWidths[col.columnKey].minWidth,
                    flex: columnWidths[col.columnKey].flex,
                    // On web, omit fixed width so flex fills the container (prevents right-side gap)
                    ...(Platform.OS !== "web" && { width: columnWidths[col.columnKey].minWidth }),
                  },
                ];
                const cellContent = (
                  <View style={styles.headerCellContent}>
                    <Text
                      style={[
                        [styles.headerCellText, { color: headerTextColor }],
                        useHighContrastText && { color: colors.text.primary },
                      ]}
                      numberOfLines={1}
                      ellipsizeMode="tail"
                    >
                      {col.header}
                    </Text>
                    {isSortable && (
                      <View style={styles.sortIconContainer}>
                        {!isSorted ? (
                          <MaterialIcons
                            name="unfold-more"
                            size={16}
                            color={headerTextColor}
                            style={styles.sortIcon}
                          />
                        ) : sortConfig?.direction === "asc" ? (
                          <MaterialIcons
                            name="keyboard-arrow-up"
                            size={16}
                            color={headerTextColor}
                            style={styles.sortIcon}
                          />
                        ) : (
                          <MaterialIcons
                            name="keyboard-arrow-down"
                            size={16}
                            color={headerTextColor}
                            style={styles.sortIcon}
                          />
                        )}
                      </View>
                    )}
                  </View>
                );
                return isSortable ? (
                  <TouchableOpacity
                    key={col.columnKey}
                    onPress={() => handleSort(col.columnKey)}
                    style={cellStyle}
                  >
                    {cellContent}
                  </TouchableOpacity>
                ) : (
                  <View key={col.columnKey} style={cellStyle}>
                    {cellContent}
                  </View>
                );
              })}
            </View>
            {paginatedData.length > 0 ? (
              <View
                style={
                  Platform.OS === "ios" || Platform.OS === "android"
                    ? { flex: 1, maxHeight: 800 }
                    : { flex: 1 }
                }
              >
                <FlatList
                  data={paginatedData}
                  renderItem={renderRow}
                  keyExtractor={(_, index) => index.toString()}
                  showsVerticalScrollIndicator={true}
                  scrollEnabled={
                    Platform.OS === "ios" || Platform.OS === "android"
                  }
                  style={{ flexGrow: 1 }}
                  nestedScrollEnabled={true}
                  scrollEventThrottle={16}
                />
              </View>
            ) : (
              <View style={[styles.noDataContainer, (isTabletDark || (isDark && Platform.OS === 'web')) && { borderBottomColor: '#363636' }]}>
                <Text style={[styles.noDataText, (isTabletDark || (isDark && Platform.OS === 'web')) && { color: '#B0B0B0' }]}>No data to display</Text>
              </View>
            )}
          </View>
        </ScrollView>
        {sortedData.length > 0 && renderPaginationControls()}
        <LHOverlay
          visible={overlayVisible}
          onDismiss={closeOverlay}
          title={
            overlayContent ? `${overlayContent.type} Details` : "Row Details"
          }
          size="large"
        >
          {overlayContent && (
            <View style={styles.overlayContent}>
              <View style={styles.overlaySection}>
                <Text style={styles.overlaySectionTitle}>Summary</Text>
                <View
                  style={[
                    styles.overlaySummary,
                    Platform.OS !== "web" && styles.overlaySummaryMobile,
                  ]}
                >
                  {columns.map((col) => (
                    <View
                      key={col.columnKey}
                      style={[
                        styles.overlayField,
                        Platform.OS !== "web" && styles.overlayFieldMobile,
                      ]}
                    >
                      <Text style={[styles.overlayFieldLabel, isTabletDark && { color: '#B0B0B0' }]}>
                        {col.header}:
                      </Text>
                      <Text style={[styles.overlayFieldValue, isTabletDark && { color: '#E0E0E0' }]}>
                        {String(overlayContent[col.columnKey])}
                      </Text>
                    </View>
                  ))}
                </View>
              </View>

              {overlayContent.observationNotes && (
                <View style={styles.overlaySection}>
                  <Text style={styles.overlaySectionTitle}>
                    Observation Notes
                  </Text>
                  <Text style={[styles.overlayAdditionalContent, isTabletDark && { color: '#E0E0E0' }]}>
                    {overlayContent.observationNotes}
                  </Text>
                </View>
              )}

              {overlayContent.miscellaneousObservations && (
                <View style={styles.overlaySection}>
                  <Text style={styles.overlaySectionTitle}>
                    Miscellaneous Observations
                  </Text>
                  <Text style={[styles.overlayAdditionalContent, isTabletDark && { color: '#E0E0E0' }]}>
                    {overlayContent.miscellaneousObservations}
                  </Text>
                </View>
              )}

              <View style={styles.overlaySection}>
                <Text style={styles.overlaySectionTitle}>
                  Additional Information
                </Text>
                <View
                  style={[
                    styles.overlaySummary,
                    Platform.OS !== "web" && styles.overlaySummaryMobile,
                  ]}
                >
                  {Object.entries(overlayContent)
                    .filter(
                      ([key]) =>
                        !columns.some((col) => col.columnKey === key) &&
                        key !== "observationNotes" &&
                        key !== "miscellaneousObservations"
                    )
                    .map(([key, value]) => (
                      <View
                        key={key}
                        style={[
                          styles.overlayField,
                          Platform.OS !== "web" && styles.overlayFieldMobile,
                        ]}
                      >
                        <Text style={[styles.overlayFieldLabel, isTabletDark && { color: '#B0B0B0' }]}>
                          {key.charAt(0).toUpperCase() +
                            key.slice(1).replace(/([A-Z])/g, " $1")}
                          :
                        </Text>
                        <Text style={[styles.overlayFieldValue, isTabletDark && { color: '#E0E0E0' }]}>
                          {typeof value === "boolean"
                            ? value
                              ? "Yes"
                              : "No"
                            : String(value)}
                        </Text>
                      </View>
                    ))}
                </View>
              </View>
            </View>
          )}
        </LHOverlay>
      </View>
    </TouchableWithoutFeedback>
  );
};

export default LHDataTable;

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  toggleContainer: {
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    height: "100%",
    minHeight: 40,
  },
  cellContainer: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingVertical: 8, // design spacing-8 top & bottom
    flex: 0, // Prevent vertical stretching
    overflow: "hidden",
    minHeight: 48,
    height: 48,
  },
  centerContent: {
    alignItems: "center",
    justifyContent: "center",
  },
  header: {
    fontSize: 20,
    fontWeight: "bold",
    color: "#3A3A3A",
  },
  description: {
    fontSize: 14,
    color: "#616161",
    // marginBottom: Platform.OS === "web" ? 20 : 0,
  },
  headerRow: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "transparent",
    height: 48, // design height
    minHeight: 48,
    borderBottomWidth: 2,
    borderBottomColor: "#D7E5F8",
    ...(Platform.OS !== 'web' && { width: "100%" }),
    zIndex: 1,
  },
  row: {
    flexDirection: "row",
    paddingVertical: 0, // padding moved to cell level to match design
    borderBottomWidth: 2,
    borderBottomColor: "#D7E5F8",
    ...(Platform.OS !== 'web' && { width: "100%" }), // Take full width
    flex: 1,
    minHeight: 48,
    height: 48,
    alignItems: "center", // vertically center cell content
  },
  selectedRow: {
    backgroundColor: "#e0f7fa",
  },
  cellTouchable: {
    flex: 1,
    justifyContent: "center",
    width: "100%", // Take full width
    overflow: "hidden", // Prevent content from overflowing
    paddingHorizontal: 4, // Add horizontal padding to give the text some breathing room
    paddingVertical: 0, // vertical padding handled by container
    // alignItems controlled dynamically based on platform
  },
  cell: {
    fontSize: Platform.OS === "web" ? 14 : 12,
    flexShrink: 1, // Allow text to shrink if needed
    width: "100%", // Take full width of container
    flexWrap: "wrap", // Enable text wrapping
    flexDirection: "row", // Needed for proper text wrapping
    textAlignVertical: "center", // vertical align middle for platforms that support it
    fontWeight: "400", // regular weight for cell text
    fontFamily: FontFamily.regular,
    // Left align only for web and iOS iPad, center for iOS mobile
  },
  headerCell: {
    fontWeight: "700",
    fontSize: Platform.OS === "web" ? 12 : 12, // XSmall equivalent
    textAlign: "center",
    flexWrap: "nowrap",
    lineHeight: 18,
    width: "100%",
    color: "#3A3A3A",
    backgroundColor: "transparent",
    zIndex: 2,
    textTransform: "uppercase", // design requires uppercase
    letterSpacing: 0,
  },
  headerCellText: {
    // keep the text to its content size so the icon sits right next to it
    fontWeight: "700",
    fontSize: 12,
    color: "#616161",
    textTransform: "uppercase",
    lineHeight: 18,
    includeFontPadding: false,
    padding: 0,
    // ensure the text doesn't stretch to full width
    width: "auto",
    flexShrink: 1,
    textAlign: "left",
    fontFamily: FontFamily.bold,
  },
  firstCellText: {
    color: "#005CBC",
    fontWeight: "500",
    fontFamily: FontFamily.regular,
  },
  otherCellText: {
    color: "#3A3A3A",
    fontFamily: FontFamily.regular,
  },
  typeCell: {
    color: "#FF6720",
    fontWeight: "bold",
    fontFamily: FontFamily.bold,
    // textDecorationLine: "underline",
  },
  // Overlay styles
  overlayContent: {
    padding: Platform.OS === "web" ? 16 : 12,

  },
  overlaySection: {
    marginBottom: Platform.OS === "web" ? 24 : 16,
    borderWidth: 1,
    borderColor: "#eaeaea",
    borderRadius: 8,
    overflow: "hidden",
  },
  overlaySectionTitle: {
    fontSize: Platform.OS === "web" ? 16 : 14,
    fontWeight: "bold",
    backgroundColor: "#0066CC",
    color: "#FFFFFF",
    padding: Platform.OS === "web" ? 12 : 10,
    fontFamily: FontFamily.regular,
  },
  overlaySummary: {
    padding: Platform.OS === "web" ? 16 : 12,
    flexDirection: "row",
    flexWrap: "wrap",
  },
  overlaySummaryMobile: {
    flexDirection: "column",
  },
  overlayField: {
    width: "50%",
    marginBottom: Platform.OS === "web" ? 12 : 8,
    paddingRight: Platform.OS === "web" ? 8 : 0,
  },
  overlayFieldMobile: {
    width: "100%",
    paddingRight: 0,
    marginBottom: 12,
  },
  overlayFieldLabel: {
    fontSize: Platform.OS === "web" ? 14 : 13,
    fontWeight: "bold",
    color: "#616161",
    marginBottom: 4,
    fontFamily: FontFamily.regular,
  },
  overlayFieldValue: {
    fontSize: Platform.OS === "web" ? 16 : 14,
    color: "#212121",
    fontFamily: FontFamily.regular,
  },
  overlayAdditionalContent: {
    padding: Platform.OS === "web" ? 16 : 12,
    fontSize: Platform.OS === "web" ? 15 : 14,
    lineHeight: Platform.OS === "web" ? 22 : 20,
    color: "#424242",
    fontFamily: FontFamily.regular,
  },
  noDataContainer: {
    padding: 20,
    alignItems: "center",
    justifyContent: "center",
    borderBottomWidth: 1,
    borderBottomColor: "#d7e5f8",
  },
  noDataText: {
    color: "#616161",
    fontSize: 16,
    fontFamily: FontFamily.regular,
  },
  // Pagination styles
  paginationContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingHorizontal: 10,
    paddingVertical: 12,
    borderTopWidth: 1,
    borderTopColor: "#E0E0E0",
  },
  rowsPerPageContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    flexShrink: 1,
  },
  rowsPerPageLabel: {
    fontSize: 14,
    color: "#616161",
    fontWeight: "400",
    fontFamily: FontFamily.regular,
  },
  dropdownContainer: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderWidth: 1,
    borderColor: "#D0D0D0",
    borderRadius: 4,
    backgroundColor: "#FFFFFF",
    minWidth: 60,
    justifyContent: "space-between",
  },
  dropdownValue: {
    fontSize: 14,
    color: "#3A3A3A",
    fontWeight: "400",
    fontFamily: FontFamily.regular,
  },
  pageNavigationContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 0,
    flexShrink: 1,
  },
  navigationButton: {
    padding: 8,
    borderRadius: 4,
    backgroundColor: "transparent",
    borderWidth: 1,
    borderColor: "#D0D0D0",
    alignItems: "center",
    justifyContent: "center",
  },
  navigationButtonNoBorder: {
    padding: 8,
    borderRadius: 4,
    backgroundColor: "transparent",
    alignItems: "center",
    justifyContent: "center",
  },
  navigationButtonDisabled: {
    borderColor: "#F0F0F0",
    backgroundColor: "#F8F8F8",
  },
  pageIndicatorContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    flexShrink: 1,
  },
  currentPageBox: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderWidth: 1,
    borderColor: "#005CBC",
    borderRadius: 4,
    backgroundColor: "#FFFFFF",
    alignItems: "center",
    justifyContent: "center",
  },
  currentPageText: {
    fontSize: 14,
    color: "#2D2D2D",
    fontWeight: "600",
    fontFamily: FontFamily.regular,
  },
  pageText: {
    fontSize: 14,
    color: "#2D2D2D",
    fontWeight: "400",
    fontFamily: FontFamily.regular,
  },
  pageOfText: {
    fontSize: 14,
    color: "#616161",
    fontWeight: "400",
    fontFamily: FontFamily.regular,
  },
  totalPagesText: {
    fontSize: 14,
    color: "#616161",
    fontWeight: "700",
    fontFamily: FontFamily.bold,
  },
  dropdownOptionsContainer: {
    position: "absolute",
    bottom: "100%",
    left: 0,
    right: 0,
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: "#D0D0D0",
    borderRadius: 4,
    marginBottom: 6,
    zIndex: 1000,
  },
  dropdownOption: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#F0F0F0",
  },
  dropdownOptionSelected: {
    backgroundColor: "#F5F9FF",
  },
  dropdownOptionText: {
    fontSize: 14,
    color: "#3A3A3A",
    fontWeight: "400",
    fontFamily: FontFamily.regular,
  },
  dropdownOptionTextSelected: {
    color: "#005CBC",
    fontWeight: "600",
    fontFamily: FontFamily.regular,
  },
  // Mobile pagination styles (Figma-based)
  mobilePaginationContainer: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    paddingVertical: 16,
    backgroundColor: "transparent", // Removed background color
  },
  mobilePaginationBox: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "transparent", // Removed background color
    borderRadius: 4,
    borderWidth: 1,
    borderColor: "#D7E5F8",
    overflow: "hidden",
  },
  mobilePaginationCell: {
    minHeight: 32,
    minWidth: 32,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  mobilePaginationArrowCell: {
    backgroundColor: "transparent", // Removed background color
  },
  mobilePaginationCurrentCell: {
    backgroundColor: "#005CBC",
  },
  mobilePaginationNextCell: {
    backgroundColor: "#FBFCFE",
  },
  mobilePaginationCellDisabled: {
    opacity: 0.5,
  },
  mobilePaginationCurrentText: {
    fontSize: 14,
    fontWeight: "700",
    color: "#FFFFFF",
    textAlign: "center",
    fontFamily: FontFamily.regular,
  },
  mobilePaginationNextText: {
    fontSize: 14,
    fontWeight: "400",
    color: "#005CBC",
    textAlign: "center",
    fontFamily: FontFamily.regular,
  },
  // Header cell content container
  headerCellContent: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
    width: "100%",
    gap: 12,
  },
  // Sort icon container
  sortIconContainer: {
    alignItems: "center",
    justifyContent: "center",
  },
  // Sort icon styling
  sortIcon: {
    alignSelf: "center",
  },
});
